<template>
  <v-container fluid>
    <v-skeleton-loader :loading="loading" :types="skeleton" type="article" class="mb-6">
      <v-card class="elevation-1 mb-6 rounded-lg">
        <v-card-title class="pa-7">
          Zákazník
          <v-spacer></v-spacer>
          <UserRole v-if="customer.user" :role="customer.user.role" class="mr-1" />
          <UserStatus v-if="customer.user" :status="customer.user.status" />
        </v-card-title>
        <v-card-text class="pa-7">
          <v-row>
            <v-col cols="12">
              <v-row class="pb-6">
                <v-col cols="4" class="py-0">
                  <div class="request-label">ID</div>
                  <div class="request-value mt-2">
                    {{ customer.id }}
                  </div>
                </v-col>
                <v-col cols="4" class="py-0">
                  <div class="request-label">Daktela</div>
                  <div v-if="customer.externalId" class="request-value mt-2">
                    <a
                      target="_blank"
                      :href="`https://enkidoo.daktela.com/crm/contacts/update/${customer.externalId}`"
                    >
                      Zobrazit
                    </a>
                    <v-icon small color="primary">mdi-open-in-new</v-icon>
                  </div>
                  <div v-else class="request-value mt-2">
                    <v-btn color="primary" depressed small @click="createContact"> Vytvořit </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-row class="pb-6">
                <v-col cols="4" class="py-0">
                  <div class="request-label">Oslovení</div>
                  <div class="request-value mt-2">
                    {{ customer.gender ? customer.gender.text : '-' }}
                  </div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-row class="pb-6">
                <v-col cols="4" class="py-0">
                  <div class="request-label">Jméno</div>
                  <div class="request-value mt-2">
                    {{ customer.fullName ? customer.fullName : '-' }}
                  </div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-row class="pb-6">
                <v-col cols="4" class="py-0">
                  <div class="request-label">Titul před</div>
                  <div class="request-value mt-2">
                    {{ customer.titleBefore ? customer.titleBefore : '-' }}
                  </div>
                </v-col>
                <v-col cols="auto" class="py-0">
                  <div class="request-label">Titul za</div>
                  <div class="request-value mt-2">
                    {{ customer.titleAfter ? customer.titleAfter : '-' }}
                  </div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-row class="pb-6">
                <v-col cols="4" class="py-0">
                  <div class="request-label">Datum narození</div>
                  <div class="request-value mt-2">
                    {{ customer.birthday ? $dayjs(customer.birthday).format('D.M.YYYY') : '-' }}
                  </div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-row class="pb-6">
                <v-col cols="4" class="py-0">
                  <div class="request-label">Telefon</div>
                  <div class="request-value mt-2">
                    {{ customer.phone ? formatPhoneNumber(customer.phone) : '-' }}
                  </div>
                </v-col>
                <v-col cols="auto" class="py-0">
                  <div class="request-label">E-mail</div>
                  <div class="request-value mt-2">
                    {{ customer.email ? customer.email : '-' }}
                  </div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-row class="pb-6">
                <v-col cols="4" class="py-0">
                  <div class="request-label">Číslo bankovního účtu</div>
                  <div class="request-value mt-2">
                    {{
                      customer.bankAccountNumber
                        ? customer.bankAccountNumber + '/' + customer.bankCode
                        : '-'
                    }}
                  </div>
                </v-col>
                <v-col cols="4" class="py-0">
                  <div class="request-label">Číslo SIPO</div>
                  <div class="request-value mt-2">
                    {{ customer.sipoNumber ? customer.sipoNumber : '-' }}
                  </div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="pl-7 pr-7 pb-7">
          <v-spacer></v-spacer>
          <v-btn
            v-if="customer.user"
            type="submit"
            height="40px"
            min-width="130px"
            style="color: #e53935"
            outlined
            depressed
            @click="removeUserAccount()"
          >
            Smazat účet
          </v-btn>
          <template>
            <v-btn
              type="submit"
              height="40px"
              min-width="130px"
              color="primary"
              depressed
              :to="{ name: 'customers.edit', params: { customerId: customer.id } }"
            >
              Editovat
            </v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-skeleton-loader>
    <v-row>
      <v-col>
        <v-skeleton-loader :loading="loading" :types="skeleton" type="article" class="mb-6">
          <div>
            <AddressDetail
              v-if="customer.address"
              :id="customer.address.id"
              v-model="customer.address"
              :customer-id="customer.id"
              title="Adresa"
            />
          </div>
        </v-skeleton-loader>
      </v-col>
      <v-col>
        <v-skeleton-loader :loading="loading" :types="skeleton" type="article" class="mb-6">
          <div>
            <AddressDetail
              v-if="customer.contactAddress"
              :id="customer.contactAddress.id"
              v-model="customer.contactAddress"
              :customer-id="customer.id"
              title="Kontaktní adresa"
            />
          </div>
        </v-skeleton-loader>
      </v-col>
    </v-row>
    <CustomerDocuments :id="$route.params.customerId" @documentConfirmed="loadCustomer" />
    <CustomerOrders :id="$route.params.customerId" />
    <CustomerDaktelaActivities :id="$route.params.customerId" />
    <CustomerPromoCodes :id="$route.params.customerId" />
    <CustomerOffers :id="$route.params.customerId" />
    <CustomerDuplicates :id="$route.params.customerId" />
    <CustomerRevisions :id="$route.params.customerId" />
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import CustomerRevisions from '@/components/revision/CustomerRevisions'
import { formatPhoneNumber } from '@/components/mixins/formatPhoneNumber'
import AddressDetail from '@/views/pages/address/AddressDetail'
import CustomerDuplicates from '@/views/pages/customer/CustomerDuplicates'
import CustomerOrders from '../../../components/customer/CustomerOrders'
import CustomerPromoCodes from '../../../components/customer/CustomerPromoCodes'
import UserRole from '@/components/customer/UserRole'
import UserStatus from '@/components/customer/UserStatus'
import CustomerDocuments from './CustomerDocuments'
import CustomerOffers from '../../../components/customer/CustomerOffers'
import CustomerDaktelaActivities from '../../../components/customer/CustomerDaktelaActivities.vue'

export default {
  name: 'CustomerDetail',
  components: {
    CustomerDaktelaActivities,
    CustomerOffers,
    CustomerDocuments,
    UserStatus,
    UserRole,
    CustomerPromoCodes,
    CustomerDuplicates,
    AddressDetail,
    CustomerOrders,
    CustomerRevisions,
  },
  mixins: [formatPhoneNumber],
  data() {
    return {
      skeleton: {
        article: 'heading, paragraph@2',
      },
      customer: {},
      loading: false,
      saving: false,
      signing: false,
    }
  },
  created() {
    this.loadCustomer()
  },
  methods: {
    ...mapActions({
      getCustomer: 'customer/getCustomer',
      deleteUser: 'user/deleteUser',
      createDaktelaContact: 'daktela/createContact',
    }),
    loadCustomer() {
      this.loading = true
      this.getCustomer(this.$route.params.customerId)
        .then((response) => {
          this.customer = response.data
        })
        .finally(() => {
          this.loading = false
        })
    },
    removeUserAccount() {
      this.$confirm('', {
        title: 'Smazat uživatelský účet?',
        buttonTrueText: 'Ano',
        buttonFalseText: 'Ne',
        icon: '',
        color: 'black',
      }).then((result) => {
        if (result) {
          this.loading = true
          this.deleteUser(this.customer.user.id)
            .then(() => {
              this.$router.push({
                name: 'customers',
              })
            })
            .finally(() => {
              this.loading = false
            })
        }
      })
    },
    createContact() {
      this.$confirm('', {
        title: 'Opravdu vytvořit kontakt v Daktele?',
        buttonTrueText: 'Ano',
        buttonFalseText: 'Ne',
        icon: '',
        color: 'black',
      }).then((result) => {
        if (result) {
          this.loading = true
          this.createDaktelaContact(this.$route.params.customerId)
            .then(() => {
              this.loadCustomer()
            })
            .finally(() => {
              this.loading = false
            })
        }
      })
    },
    back() {
      this.$router.push({
        name: 'customers',
      })
    },
  },
}
</script>
